import { cva, type VariantProps } from 'class-variance-authority'
import { forwardRef, type ButtonHTMLAttributes, type MouseEvent } from 'react'
import { cn } from '@/lib/cn'

/**
 * Variants map to intent, not to looks. See docs/design-system.md §6.1.
 *
 * `generate` carries the spectrum gradient and is reserved for the single
 * primary creation action. One per screen, never anywhere else.
 * Button text names the outcome: "Оплатить 890 ₽", not "Отправить".
 *
 * Heights stay 32 / 40 / 48 (sm / md / lg); on a touch screen (`pointer: coarse`, the admin as
 * a Telegram Mini App) every size grows to 44 px so a thumb never misses (WP-07, WP-15).
 *
 * Disabled looks the same for every variant (WP-07): a neutral surface-3 button with ink-3 text
 * and a hairline edge, readable in both themes — not a faded copy of its colour, which read as
 * «another colour» rather than «not now». Say what is missing in a caption next to it.
 */
const button = cva(
  [
    'inline-flex items-center justify-center gap-2 rounded-sm border border-transparent font-ui font-semibold',
    'transition-all duration-micro ease',
    'active:scale-[0.98]',
    '[@media(pointer:coarse)]:min-h-11',
    'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan',
    // A printout is a report: nothing on it can be pressed (WP-25).
    'print:hidden',
  ],
  {
    variants: {
      variant: {
        generate: 'bg-spectrum text-on-spectrum shadow-glow-magenta',
        primary: 'bg-cyan text-void',
        secondary: 'bg-surface-3 text-ink-1 border-hairline-strong hover:border-field hover:bg-surface-2',
        ghost: 'bg-transparent text-ink-2 hover:text-ink-1',
        destructive: 'bg-danger-soft text-danger border-danger-line',
      },
      size: {
        sm: 'h-8 px-3 text-caption',
        md: 'h-10 px-4 text-body',
        lg: 'h-12 px-6 text-body-lg',
      },
      full: { true: 'w-full' },
    },
    defaultVariants: { variant: 'secondary', size: 'md' },
  },
)

/** One disabled look for every variant (no gradient, no glow, no fade). A button switched off
 *  with `aria-disabled` (it keeps keyboard focus: «Сохранить изменения» with nothing to save)
 *  looks the same as a `disabled` one (wave-4 review: it was a 40% faded cyan); a busy button
 *  (`aria-busy`, `loading`) is not «off» and keeps its colour (§6.1). */
// Written out in full: Tailwind finds classes by their literal text, a `${…}`-built name is never
// generated (wave-4 review).
const DISABLED = [
  'disabled:pointer-events-none disabled:opacity-100',
  'disabled:border-hairline disabled:bg-surface-3 disabled:bg-none disabled:text-ink-3 disabled:shadow-none',
  '[&[aria-disabled=true]:not([aria-busy=true])]:cursor-not-allowed',
  '[&[aria-disabled=true]:not([aria-busy=true])]:opacity-100',
  '[&[aria-disabled=true]:not([aria-busy=true])]:border-hairline',
  '[&[aria-disabled=true]:not([aria-busy=true])]:bg-surface-3',
  '[&[aria-disabled=true]:not([aria-busy=true])]:bg-none',
  '[&[aria-disabled=true]:not([aria-busy=true])]:text-ink-3',
  '[&[aria-disabled=true]:not([aria-busy=true])]:shadow-none',
  '[&[aria-disabled=true]:not([aria-busy=true])]:active:scale-100',
]

/** The button look for a link that navigates (`<Link className={buttonVariants(…)}>`). */
export const buttonVariants = button

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  /** a request is in flight: the button keeps its colour and width, ignores presses and says
   *  so to assistive tech (`aria-busy`) */
  loading?: boolean
  /** the label while loading, a gerund: «Сохраняю…», «Вхожу…». Without it the label stays. */
  loadingLabel?: string
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, full, loading, loadingLabel, children, disabled, onClick, ...props }, ref) => {
    // Loading is not `disabled`: a disabled button drops keyboard focus and would take the
    // neutral disabled look. It stays focusable and swallows presses (and the form submit an
    // Enter in a field triggers) until the request settles.
    const busy = Boolean(loading)
    function onPress(e: MouseEvent<HTMLButtonElement>) {
      if (busy) {
        e.preventDefault()
        return
      }
      onClick?.(e)
    }
    return (
      <button
        ref={ref}
        className={cn(button({ variant, size, full }), DISABLED, busy && 'cursor-progress active:scale-100', className)}
        disabled={disabled}
        aria-disabled={busy || undefined}
        aria-busy={busy || undefined}
        onClick={onPress}
        {...props}
      >
        {busy && loadingLabel ? (
          // Both labels share one grid cell: the button never narrows while it waits.
          <span className="inline-grid">
            <span className="invisible col-start-1 row-start-1 inline-flex items-center justify-center gap-2" aria-hidden>
              {children}
            </span>
            <span className="col-start-1 row-start-1">{loadingLabel}</span>
          </span>
        ) : (
          children
        )}
      </button>
    )
  },
)

Button.displayName = 'Button'
